import { Form } from "antd";
import { CAMPAIGN_TYPE, COMBO_CAMPAIGN, DISCOUNT_TYPE, PRODUCT_TYPE, TYPE_LIMIT } from "features/Campaign/constants";
import {
  getProductPromoConfirmBudget,
  getSellerSegmentTypeByID,
  mapProductPromoTierBudgetField,
} from "features/Campaign/utils/productPromoTargetCustomer";
import { sanitizePromotionCampaignInput } from "features/Campaign/utils/sanitizeCampaignInput";
import { useGetDetailPromotionCampaign } from "hooks/campaign";
import { useUpdateCampaign } from "hooks/campaign/campaign";
import { convertToEditFormValue } from "hooks/campaign/converter";
import { useUploadDocument } from "hooks/upload-document";
import { useGetSellerUserSegments } from "hooks/seller";
import { t } from "i18next";
import { cloneDeep, forEach, unset } from "lodash";
import { useEffect, useMemo, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { convertIsoStringToDateTime, endOfMomentToISOString, startOfMomentToISOString } from "utils/dateTime";
import { getQuery, notify } from "utils/helperFuncs";

function convertDataGift(data) {
  return data.map((item) => {
    if (item.promotionCampaignTiers) {
      item.promotionCampaignTiers = item.promotionCampaignTiers.map((tier) => {
        if (tier.conditionProducts) {
          tier.conditionProducts = tier.conditionProducts.map((product) => {
            return {
              conditionPromotionProducts: product.conditionPromotionProducts
                ?.filter((item) => item?.id || item?.productID)
                .map((item) => ({
                  productID: item.id || item?.productID,
                  type: PRODUCT_TYPE.CONDITION_PRODUCT,
                })),
              quantity: product.quantity,
              type: product.type,
            };
          });
        }

        if (tier.conditionGifts) {
          tier.conditionGifts = tier.conditionGifts.map((gift) => {
            // Check if conditionPromotionProducts is array (from import Excel) or object (from form)
            const isConditionPromoProductsArray = Array.isArray(gift.conditionPromotionProducts);

            const giftConditionPromotionProducts = isConditionPromoProductsArray
              ? gift.conditionPromotionProducts
                  .filter((prod) => prod?.productID || prod?.id || prod?.product?.id)
                  .map((prod) => ({
                    productID: prod?.productID || prod?.id || prod?.product?.id,
                    budgetQuantity: prod?.budgetQuantity || 0,
                    productWarranty: prod?.productWarranty || 0,
                    type: PRODUCT_TYPE.GIFT_PRODUCT,
                  }))
              : Object.keys(gift.conditionPromotionProducts || {})
                  .filter((key) => !isNaN(key))
                  .map((key) => {
                    const convertProductWarranty = () => {
                      if (gift && gift[key] && gift[key].productWarranty === 0) {
                        return 0;
                      } else {
                        return gift?.[key]?.productWarranty || gift.conditionPromotionProducts?.[key]?.warranty;
                      }
                    };

                    return {
                      productID:
                        gift.conditionPromotionProducts?.[key]?.id || gift.conditionPromotionProducts?.[key]?.productID,
                      budgetQuantity: gift?.[key]?.budgetQuantity,
                      productWarranty: convertProductWarranty(),
                      type: PRODUCT_TYPE.GIFT_PRODUCT,
                    };
                  })
                  .filter((item) => item?.productID);

            return {
              conditionPromotionProducts: giftConditionPromotionProducts,
              quantity: gift.quantity,
              type: gift.type,
            };
          });
        }

        return {
          ...tier,
        };
      });
    }

    const conditionProductGroups = item.promotionCampaignTiers.map((item) => {
      const maxQuantityPerSeller = item?.limitType === TYPE_LIMIT.NO_LIMIT ? null : item.maxQuantityPerSeller;
      return {
        type: item.type,
        maxQuantityPerSeller: maxQuantityPerSeller,
        limitType: item.limitType,
        conditionProductGroups: [...item.conditionProducts, ...item.conditionGifts],
      };
    });

    const newData = {
      type: item.type,
      promotionCampaignTiers: conditionProductGroups,
    };
    return newData;
  });
}

const useEditForm = () => {
  const [form] = Form.useForm();
  const { id } = useParams();
  const GET_QUERY = getQuery();
  const history = useHistory();
  const [promotionCampaignTierGroups, setPromotionCampaignTierGroups] = useState([]);
  const [visibleConfirmModal, setVisibleConfirmModal] = useState(false);
  const [dataConfirmModal, setDataConfirmModal] = useState(null);
  const [isApplyWithPromotion, setIsApplyWithPromotion] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const DEFAULT_HIGHLIGHT_CASH_DISCOUNT = "Tiết kiệm % khi thanh toán Chuyển Khoản/Tiền mặt ngay";

  const getCashDiscountHighlight = (discountRate) => {
    return discountRate === null || discountRate === undefined || discountRate === ""
      ? DEFAULT_HIGHLIGHT_CASH_DISCOUNT
      : `Tiết kiệm ${discountRate}% khi thanh toán Chuyển Khoản/Tiền mặt ngay`;
  };

  const { data, loading } = useGetDetailPromotionCampaign({
    id: id,
    withCart: false,
    campaignType: GET_QUERY.campaignType,
    skipTierGroups: GET_QUERY.campaignType === "ORDER_PROMO" || GET_QUERY.campaignType === "LUCKY_WHEEL",
  });

  const initValues = useMemo(() => convertToEditFormValue(data), [data]);

  const { data: sellerUserSegments } = useGetSellerUserSegments();

  const { handleUpload, loading: loadingImageUpload } = useUploadDocument();

  const getCampaignType = () => {
    if (GET_QUERY.campaignType === "CASH_DISCOUNT") {
      return CAMPAIGN_TYPE.CASH_DISCOUNT;
    }

    switch (GET_QUERY.campaignType) {
      case CAMPAIGN_TYPE.COMBO_PROMO:
        return CAMPAIGN_TYPE.COMBO_PROMO;
      case CAMPAIGN_TYPE.PRODUCT_PROMO:
        return CAMPAIGN_TYPE.PRODUCT_PROMO;
      case CAMPAIGN_TYPE.CASH_DISCOUNT:
        return CAMPAIGN_TYPE.CASH_DISCOUNT;
      default:
        return null;
    }
  };

  const validateConditionProducts = (conditionProducts) => {
    return conditionProducts?.some(
      (itemProductGroup) =>
        itemProductGroup?.conditionPromotionProducts?.length === 0 ||
        itemProductGroup?.conditionPromotionProducts?.some(
          (product) =>
            !product?.id && !product?.productID && !product?.product?.id && Object.keys(product || {}).length === 0
        )
    );
  };

  const truncateDecimal = (value, decimalPlaces = 2) => {
    if (value === null || value === undefined || value === "") {
      return value;
    }

    const stringValue = String(value);
    const [integerPart = "", decimalPart = ""] = stringValue.split(".");

    if (!decimalPart) {
      return stringValue;
    }

    return `${integerPart}.${decimalPart.slice(0, decimalPlaces)}`;
  };

  function mergeCampaignTierGroups(values) {
    const discountMoneyIterable = Array.isArray(values?.promotionCampaignTierGroupsDiscountMoney)
      ? values?.promotionCampaignTierGroupsDiscountMoney
      : [];
    const fixedPriceIterable = Array.isArray(values?.promotionCampaignTierGroupsFixedPrice)
      ? values?.promotionCampaignTierGroupsFixedPrice
      : [];
    const giftIterable = Array.isArray(values?.promotionCampaignTierGroupsGift)
      ? values?.promotionCampaignTierGroupsGift
      : [];
    const productPromoIterable = Array.isArray(values?.promotionCampaignTierGroupsProductPromo)
      ? values?.promotionCampaignTierGroupsProductPromo
      : [];
    const mergePromotionCampaignTierGroups = [
      ...discountMoneyIterable,
      ...fixedPriceIterable,
      ...giftIterable,
      ...productPromoIterable,
    ];

    return mergePromotionCampaignTierGroups;
  }

  const handleOpenConfirmModal = async () => {
    // setVisibleConfirmModal(true);
    const valuesBeforeValidate = form.getFieldsValue();
    const normalizedDiscountRate = truncateDecimal(valuesBeforeValidate?.discountRate);

    if (normalizedDiscountRate !== valuesBeforeValidate?.discountRate) {
      form.setFieldsValue({
        discountRate: normalizedDiscountRate,
        highlight: getCashDiscountHighlight(normalizedDiscountRate),
      });
    }

    const values = await form.validateFields();

    let allConditionsMet = true;
    const result = mergeCampaignTierGroups(values);
    const newPromotionTierGroups = result?.map((itemTiers, index) => {
      return { ...itemTiers };
    });
    if (!result?.length) {
      const isCashDiscount = getCampaignType() === CAMPAIGN_TYPE.CASH_DISCOUNT;

      if (isCashDiscount) {
        allConditionsMet = true;
      } else {
        allConditionsMet = false;
        setVisibleConfirmModal(false);
        notify.error({
          message:
            getCampaignType() === CAMPAIGN_TYPE.PRODUCT_PROMO || getCampaignType() === CAMPAIGN_TYPE.CASH_DISCOUNT
              ? t("campaign.error.productNotYet")
              : t("campaign.error.promotionalCombosYet"),
        });
      }
    }

    if (result?.length) {
      result.forEach((itemGroups, indexGroups) => {
        itemGroups.promotionCampaignTiers.forEach((itemTiers, indexTiers) => {
          if (
            validateConditionProducts(itemTiers?.conditionProductGroups) ||
            validateConditionProducts(itemTiers?.conditionProducts) ||
            validateConditionProducts(itemTiers?.conditionGifts)
          ) {
            allConditionsMet = false;
            notify.error({
              message: t("campaign.error.checkSelectItems"),
            });
          }

          if (itemTiers.discountType === DISCOUNT_TYPE.DISCOUNT_PERCENTAGE && itemTiers.discountAmount > 99) {
            allConditionsMet = false;
            notify.error({
              message: t("campaign.error.errorValidPercent"),
            });
          }
        });
      });
    }

    if (allConditionsMet) {
      const newDataConfirmModal = {
        name: values?.promoName,
        applyWithOtherPromoTypes: values?.applyWithOtherPromoTypes,
        startDate: convertIsoStringToDateTime(values?.period?.[0]),
        endDate: convertIsoStringToDateTime(values?.period?.at(1)),
        promotionCampaignTierGroups: newPromotionTierGroups,
        maxBudgetPerSeller:
          getCampaignType() === CAMPAIGN_TYPE.PRODUCT_PROMO ? getProductPromoConfirmBudget(values) : undefined,
        sellerSegmentType: values?.sellerSegmentType,
      };
      setVisibleConfirmModal(true);
      setDataConfirmModal(newDataConfirmModal);
    }
  };

  const handleCancelConfirmModal = () => {
    setVisibleConfirmModal(false);
  };

  const handleValuesChange = (changeValue, allValue) => {
    const values = form.getFieldsValue();
    const result = mergeCampaignTierGroups(values);
    if (changeValue?.applyWithOtherPromo === false) {
      const newValues = {
        ...values,
        applyWithOtherPromoTypes: [],
      };
      form.setFieldsValue(newValues);
    }
    if (
      changeValue?.promotionCampaignTierGroupsDiscountMoney ||
      changeValue?.promotionCampaignTierGroupsFixedPrice ||
      changeValue?.promotionCampaignTierGroupsGift ||
      changeValue?.promotionCampaignTierGroupsProductPromo
    ) {
      const newPromotionTierGroups = result?.map((itemTiers, index) => {
        return { ...itemTiers };
      });
      setPromotionCampaignTierGroups(newPromotionTierGroups);
    }

    if (Object.prototype.hasOwnProperty.call(changeValue || {}, "discountRate")) {
      const isCashDiscount = getCampaignType() === CAMPAIGN_TYPE.CASH_DISCOUNT;

      if (isCashDiscount) {
        const discountRate = values?.discountRate;
        const highlight = getCashDiscountHighlight(discountRate);

        form.setFieldsValue({ highlight });
      }
    }

    setHasChanges(true);
  };

  const handleCheckIsApplyWithPromo = (value) => {
    setIsApplyWithPromotion(value);
  };

  const uploadImage = async (values) => {
    const handleImageUpload = async (item) => {
      try {
        const result = await handleUpload({ files: [item?.originFileObj] });
        return result?.[0]?.url;
      } catch (error) {
        notify.error({
          message: t("uploadDocument.uploadImageError"),
          description: error?.message,
        });
        return null;
      }
    };

    const handleImageArray = async (items) => {
      return await Promise.all(
        items.map(async (item) => {
          try {
            return item?.url || (await handleImageUpload(item));
          } catch (error) {
            notify.error({
              message: t("uploadDocument.uploadImageError"),
              description: error?.message,
            });
            return null;
          }
        })
      );
    };
    const newImagesDesktopURLs = await handleImageArray(values?.bannerDesktop || []);
    const newImagesMobileURLs = await handleImageArray(values?.bannerMobile || []);

    return {
      imageURL: newImagesDesktopURLs?.[0],
      mobileURL: newImagesMobileURLs?.[0],
    };
  };

  const { handleUpdateCampaign: handleUpdateCampaignAPI, loading: loadingUpdate } = useUpdateCampaign();
  const [loadingUpdateCampaign, setLoadingUpdateCampaign] = useState(false);

  const handleUpdateClick = async () => {
    const values = await form.getFieldsValue();

    try {
      const discountMoneyIterable = Array.isArray(values?.promotionCampaignTierGroupsDiscountMoney)
        ? values?.promotionCampaignTierGroupsDiscountMoney
        : [];
      const fixedPriceIterable = Array.isArray(values?.promotionCampaignTierGroupsFixedPrice)
        ? values?.promotionCampaignTierGroupsFixedPrice
        : [];
      const giftIterable = Array.isArray(values?.promotionCampaignTierGroupsGift)
        ? values?.promotionCampaignTierGroupsGift
        : [];
      const productPromoIterable = Array.isArray(values?.promotionCampaignTierGroupsProductPromo)
        ? [
            {
              type: COMBO_CAMPAIGN.DISCOUNT_PRODUCT,
              promotionCampaignTiers: values?.promotionCampaignTierGroupsProductPromo?.[0]?.promotionCampaignTiers?.map(
                (tier) => mapProductPromoTierBudgetField(tier, values?.sellerSegmentType)
              ),
            },
          ]
        : [];

      const startDate = startOfMomentToISOString(values?.period?.[0], true);
      const endDate = endOfMomentToISOString(values?.period?.at(1), true);

      const mergePromotionCampaignTierGroups = [...discountMoneyIterable, ...fixedPriceIterable];

      setLoadingUpdateCampaign(true);
      const imageURLs = await uploadImage(values);
      const isCashDiscount = getCampaignType() === CAMPAIGN_TYPE.CASH_DISCOUNT;

      const params = {
        name: values?.promoName,
        campaignType: getCampaignType(),
        applyWithOtherPromoTypes: values?.applyWithOtherPromoTypes,
        startDate: startDate,
        endDate: endDate,
        highlight: values?.highlight,
        description: values?.description,
        discountRate: values?.discountRate ? Number(values?.discountRate) : 0,
        linkType: "LANDING_PAGE",
        cashDiscountInfo: values?.cashDiscountInfo,
        saleChannelIDs: values?.buyChannels,
        promotionCampaignTierGroups: isCashDiscount
          ? []
          : getCampaignType() === CAMPAIGN_TYPE.COMBO_PROMO
            ? mergePromotionCampaignTierGroups
            : productPromoIterable,
        ...(getCampaignType() === CAMPAIGN_TYPE.PRODUCT_PROMO && values?.sellerSegmentID
          ? { sellerSegmentID: values.sellerSegmentID }
          : {}),
        ...(getCampaignType() === CAMPAIGN_TYPE.PRODUCT_PROMO && values?.maxBudgetPerSeller
          ? { maxBudgetPerSeller: values.maxBudgetPerSeller }
          : {}),
        ...(!isCashDiscount
          ? {
              link: values?.link,
              banner: {
                ...imageURLs,
                link: values?.link,
              },
            }
          : {}),
      };

      console.log("[DEBUG] handleUpdateClick - values from form", { // eslint-disable-line
        sellerSegmentID: values?.sellerSegmentID,
        sellerSegmentType: values?.sellerSegmentType,
        maxBudgetPerSeller: values?.maxBudgetPerSeller,
      });
      console.log("[DEBUG] handleUpdateClick - params gửi lên API", params); // eslint-disable-line

      const updatedData = cloneDeep(params);

      // filters productID and unset smallestValueInStock
      const promotionCampaignTierGroupsGift = convertDataGift(giftIterable);

      forEach(updatedData?.promotionCampaignTierGroups, (tierGroups) => {
        unset(tierGroups, "id");
        unset(tierGroups, "usedProductQuantity");
        if (tierGroups?.limitType === TYPE_LIMIT.NO_LIMIT) {
          tierGroups.maxQuantityPerSeller = null;
        }
        forEach(tierGroups?.promotionCampaignTiers, (tier) => {
          if (tier?.discountType === DISCOUNT_TYPE.DISCOUNT_MONEY) {
            unset(tier, "maxDiscountAmount");
            unset(tier, "limitType");
          }
          if (tier?.limitType === TYPE_LIMIT.NO_LIMIT) {
            tier.maxDiscountAmount = null;
            tier.maxQuantityPerSeller = null;
          }
          if (tier.type === COMBO_CAMPAIGN.DISCOUNT_MONEY) {
            unset(tier, "quantity");
          }
          forEach(tier?.conditionProductGroups, (productGroup) => {
            unset(productGroup, "smallestValueInStock");

            if (
              tierGroups?.type === COMBO_CAMPAIGN.FIXED_PRICE &&
              tier?.quantity !== undefined &&
              tier?.quantity !== null
            ) {
              productGroup.quantity = tier.quantity;
              delete tier?.quantity;
            }
            if (getCampaignType() === CAMPAIGN_TYPE.PRODUCT_PROMO) {
              const condPromo = productGroup.conditionPromotionProducts;
              const productsArray = Array.isArray(condPromo) ? condPromo : [condPromo];
              productGroup.conditionPromotionProducts = productsArray
                .filter((product) => {
                  if (!product || Object.keys(product).length === 0) return false;
                  return product?.productID || product?.id || product?.product?.id;
                })
                .map((product) => {
                  return {
                    productID: product?.productID || product?.id || product?.product?.id,
                  };
                });
            }
            if (getCampaignType() === CAMPAIGN_TYPE.COMBO_PROMO) {
              productGroup.conditionPromotionProducts = productGroup.conditionPromotionProducts
                .filter((product) => {
                  if (!product || Object.keys(product).length === 0) return false;
                  return product?.id || product?.productID || product?.product?.id;
                })
                .map((product) => {
                  const productID = product?.productID || product?.id || product?.product?.id;
                  return {
                    productID: productID,
                  };
                });
            }
          });
          return {
            ...tier,
            discountType: tier?.discountType || DISCOUNT_TYPE.DISCOUNT_MONEY,
            limitType: tier?.limitType || TYPE_LIMIT.LIMIT,
          };
        });
      });

      await handleUpdateCampaignAPI({
        id: id,
        params: sanitizePromotionCampaignInput(
          isCashDiscount
            ? updatedData
            : {
                ...updatedData,
                promotionCampaignTierGroups: [
                  ...promotionCampaignTierGroupsGift,
                  ...updatedData?.promotionCampaignTierGroups,
                ],
              }
        ),
      }).then((res) => {
        const data = res?.data?.promotionCampaign?.update;
        setLoadingUpdateCampaign(false);
        if (data?.promotionID) {
          notify.success({ message: t("campaign.form.updateCampaignSuccess") });
          history.push(`/campaign/detail/${id}`);
        } else {
          notify.error({ message: data?.errorMessage });
        }
      });
    } catch (error) {
      console.error("Error handling select product:", error);
      setLoadingUpdateCampaign(false);
      notify.error({
        message: t("campaign.form.updateCampaignError"),
        description: error?.message,
      });
    }
  };

  useEffect(() => {
    form.setFieldsValue(initValues);

    // Resolve sellerSegmentType từ sellerSegmentID khi edit (campaign-level chỉ có ID)
    if (sellerUserSegments?.length && initValues?.sellerSegmentID) {
      const resolvedType = getSellerSegmentTypeByID(sellerUserSegments, initValues.sellerSegmentID);
      if (resolvedType) {
        form.setFieldsValue({ sellerSegmentType: resolvedType });
      }
    }
  }, [form, initValues, sellerUserSegments]);

  useEffect(() => {
    const handleBeforeUnload = (event) => {
      if (hasChanges) {
        event.preventDefault();
        event.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [hasChanges]);

  return {
    form,
    handleUpdateClick,
    id,
    data,
    isApplyWithPromotion,
    handleValuesChange,
    handleCheckIsApplyWithPromo,
    promotionCampaignTiers: promotionCampaignTierGroups,
    visibleConfirmModal,
    handleOpenConfirmModal,
    handleCancelConfirmModal,
    dataConfirmModal,
    loadingDataUpdate: loading,
    loadingUpdate,
    loadingUpdateCampaign,
    initValues,
    loadingAll: loadingImageUpload || loadingUpdate || loadingUpdateCampaign,
    GET_QUERY,
  };
};

export default useEditForm;
